import {
  CoverageTiers,
  RmaLrpCommodityCodes,
  RmaLrpTypeCodes,
} from '@harvestiq/constants';
import { lrpQuoteSchema } from '@harvestiq/usda-common';
import {
  z,
  zCalendarDate,
  zCoercedBoolean,
  zDecimalNullable,
  zDecimalRequired,
} from '@harvestiq/zod';

export const getQuotesV2QuerySchema = z.object({
  headCount: z.coerce.number().int().positive().openapi({
    example: 100,
  }),
  targetWeightCwtPerHead: z.coerce.number().positive().openapi({
    example: 12.5,
  }),
  salesEffectiveDate: zCalendarDate().openapi({
    example: '2025-06-13',
  }),
  endDate: zCalendarDate().optional().openapi({
    example: '2025-09-12',
  }),
  isPre2026BeginningFarmerRancher: zCoercedBoolean().optional().openapi({
    example: false,
  }),
  isVeteranFarmerRancher: zCoercedBoolean().optional().openapi({
    example: false,
  }),
  beginningFarmerRancherCropYearCount: z.coerce
    .number()
    .int()
    .nullable()
    .optional()
    .openapi({
      example: 3,
    }),
  commodityCode: z.nativeEnum(RmaLrpCommodityCodes).openapi({
    example: RmaLrpCommodityCodes.FEEDER_CATTLE,
  }),
  typeCode: z.nativeEnum(RmaLrpTypeCodes).openapi({
    example: RmaLrpTypeCodes.STEERS_WEIGHT_1,
  }),
  endorsementLengthCount: z.coerce.number().int().optional().openapi({
    example: 13,
  }),
  coverageTier: z.nativeEnum(CoverageTiers).optional().openapi({
    example: CoverageTiers.ALL,
  }),
});

export type GetQuotesV2Query = z.infer<typeof getQuotesV2QuerySchema>;

export const getQuotesV2ResponseLrpQuoteSchema = lrpQuoteSchema
  // Using pick to exclude unnecessary legacy fields
  .pick({
    salesEffectiveDate: true,
    endDate: true,
    isPre2026BeginningFarmerRancher: true,
    isVeteranFarmerRancher: true,
    beginningFarmerRancherCropYearCount: true,
    commodityCode: true,
    typeCode: true,
    endorsementLengthCount: true,

    reinsuranceYear: true,
    targetLowWeight: true,
    targetHighWeight: true,
    barchartFuturesSymbol: true,
    savingsVsCmeTrade: true,
    livestockCoverageLevelPercent: true,
    livestockRate: true,
  })
  .extend({
    headCount: z.number().int().positive(),
    targetWeightCwtPerHead: zDecimalRequired(),

    coveragePricePerCwt: zDecimalRequired(),
    coveragePricePerHead: zDecimalRequired(),
    coveragePriceTotal: zDecimalRequired(),

    liabilityAmountPerCwt: zDecimalRequired(),
    liabilityAmountPerHead: zDecimalRequired(),
    liabilityAmountTotal: zDecimalRequired(),

    totalPremiumPerCwt: zDecimalRequired(),
    totalPremiumPerHead: zDecimalRequired(),
    totalPremiumAmount: zDecimalRequired(),

    subsidyRate: zDecimalRequired(),
    subsidyPerCwt: zDecimalRequired(),
    subsidyPerHead: zDecimalRequired(),
    subsidyTotal: zDecimalRequired(),

    producerPremiumPerCwt: zDecimalRequired(),
    producerPremiumPerHead: zDecimalRequired(),
    producerPremiumTotal: zDecimalRequired(),

    expectedEndingValuePerCwt: zDecimalRequired(),
    expectedEndingValuePerHead: zDecimalRequired(),
    expectedEndingValueTotal: zDecimalRequired(),

    cmePutPremiumPerCwt: zDecimalNullable(),
    cmePutPremiumPerHead: zDecimalNullable(),
    cmePutPremiumTotal: zDecimalNullable(),

    cmePutStrikePerCwt: zDecimalNullable(),
    cmePutStrikePerHead: zDecimalNullable(),
    cmePutStrikeTotal: zDecimalNullable(),

    cmeSettlementDate: zCalendarDate().nullable(),
  });
export type GetQuotesV2ResponseLrpQuote = z.infer<
  typeof getQuotesV2ResponseLrpQuoteSchema
>;

export const getQuotesV2ResponseSchema = z.object({
  quotes: getQuotesV2ResponseLrpQuoteSchema.array().openapi({
    externalDocs: {
      description:
        'Commodity Codes and Type Codes are provided by the RMA docs and can vary by year.',
      url: 'https://pubfs-rma.fpac.usda.gov/pub/References/actuarial_data_master/2026/index.html',
    },
    example: [
      {
        headCount: 100,
        targetWeightCwtPerHead: '10',
        salesEffectiveDate: '2025-09-17',
        endDate: '2025-12-17',
        isPre2026BeginningFarmerRancher: false,
        isVeteranFarmerRancher: false,
        beginningFarmerRancherCropYearCount: null,
        commodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
        typeCode: RmaLrpTypeCodes.STEERS_WEIGHT_2,
        endorsementLengthCount: 13,
        reinsuranceYear: 2026,
        targetLowWeight: '6',
        targetHighWeight: '10',
        barchartFuturesSymbol: 'GFF26',
        savingsVsCmeTrade: '0.39512893982808022923',
        livestockCoverageLevelPercent: '1',
        livestockRate: '0.047801',
        coveragePricePerCwt: '339.7',
        coveragePricePerHead: '3397',
        coveragePriceTotal: '339700',
        liabilityAmountPerCwt: '339.7',
        liabilityAmountPerHead: '3397',
        liabilityAmountTotal: '339700',
        totalPremiumPerCwt: '16.238',
        totalPremiumPerHead: '162.38',
        totalPremiumAmount: '16238',
        subsidyRate: '0.35',
        subsidyPerCwt: '5.683',
        subsidyPerHead: '56.83',
        subsidyTotal: '5683',
        producerPremiumPerCwt: '10.555',
        producerPremiumPerHead: '105.55',
        producerPremiumTotal: '10555',
        expectedEndingValuePerCwt: '339.702',
        expectedEndingValuePerHead: '3397.02',
        expectedEndingValueTotal: '339702',
        cmePutPremiumPerCwt: '17.45',
        cmePutPremiumPerHead: '174.5',
        cmePutPremiumTotal: '17450',
        cmePutStrikePerCwt: '340',
        cmePutStrikePerHead: '3400',
        cmePutStrikeTotal: '340000',
        cmeSettlementDate: '2025-09-17',
      },
    ],
  }),
});
export type GetQuotesV2Response = z.infer<typeof getQuotesV2ResponseSchema>;
